// 🔧 use_event_settings.js
// ✅ Purpose: Load event voting settings from Firestore
// ✅ Features:
// - Fetches settings from /events/{eventId}/settings/voting
// - No localStorage caching
// - Returns settings, loading, and error
// - Safe for public and admin pages
// - Fully commented for clarity

import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import { logger } from "../utils/logger";

export const useEventSettings = (eventId) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSettings(null);
    setError(null);

    if (!eventId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadSettings = async () => {
      setLoading(true);

      try {
        const ref = doc(
          db,
          "events",
          eventId,
          "settings",
          "voting"
        );

        const snap = await getDoc(ref);

        if (cancelled) return;

        if (snap.exists()) {
          setSettings(snap.data());

          logger.log("✅ Event settings loaded:", {
            eventId,
          });
        } else {
          setSettings(null);

          logger.warn("⚠️ Event settings not found:", {
            eventId,
          });
        }
      } catch (err) {
        if (cancelled) return;

        logger.error("❌ Failed to load event settings:", {
          value: err,
        });

        setError(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  return {
    settings,
    loading,
    error,
  };
};